import { useEffect, useState } from "react";
import { Navigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button, RouterButtonLink } from "../components/Button";
import { Card } from "../components/Card";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { Container } from "../components/Container";
import { FormActions, FormActionsStart } from "../components/FormActions";
import { InfoBox } from "../components/InfoBox";
import { LoadingContainer } from "../components/LoadingContainer";
import { PageDetailHeader } from "../components/PageDetailHeader";
import { useCatalogScope } from "../hooks/useCatalogScope";
import { CATALOG_NEW_ITEM_ID, useCatalogNav } from "./catalog/catalogNav";
import { LayoutForm } from "./components/LayoutForm";
import {
  useGetLayoutQuery,
  useUpdateLayoutMutation,
  useDeleteLayoutMutation,
} from "../query/layoutsQuery";
import type { RestCreateLayoutType } from "@KassaroAB/shared/types/layout";

const FORM_ID = "catalog-layout-form";

export const LayoutFormView = () => {
  const { t } = useTranslation();
  const { setDirty, allowNavigate } = useCatalogNav();
  const { scope, pagePath } = useCatalogScope("layouts");
  const { itemId } = useParams<{ itemId: string }>();
  const isCreating = itemId === CATALOG_NEW_ITEM_ID;
  const layoutId = !isCreating && itemId ? itemId : null;

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [savedTick, setSavedTick] = useState(0);

  const {
    data: layout,
    isLoading,
    error: loadError,
  } = useGetLayoutQuery(scope, layoutId);
  const {
    mutate: updateMutation,
    error: updateError,
    isPending: isUpdatePending,
  } = useUpdateLayoutMutation(scope);
  const {
    mutate: deleteMutation,
    error: deleteError,
    isPending: isDeletePending,
  } = useDeleteLayoutMutation(scope);

  const isSaving = isUpdatePending;
  const missing = !!layoutId && !isLoading && !layout;

  const handleSubmit = (data: RestCreateLayoutType) => {
    if (!layoutId) return;
    updateMutation(
      { layoutId, layout: data },
      {
        onSuccess: () => {
          setSuccessMessage(t("layoutsView.successUpdated"));
          setSavedTick((tick) => tick + 1);
        },
        onError: () => setSuccessMessage(null),
      },
    );
  };

  const confirmDelete = () => {
    if (!layoutId) return;
    deleteMutation(layoutId, {
      onSuccess: () => {
        setDeleteOpen(false);
        allowNavigate(pagePath);
      },
    });
  };

  useEffect(() => {
    if (!successMessage) return;
    const timer = setTimeout(() => setSuccessMessage(null), 3000);
    return () => clearTimeout(timer);
  }, [successMessage]);

  if (isCreating) {
    return <Navigate to={pagePath} replace />;
  }

  if (!scope) {
    return null;
  }

  const title = layout?.name ?? t("layoutsView.title");
  const breadcrumbCurrent = missing
    ? t("catalog.itemNotFound")
    : (layout?.name ?? t("common.loading"));
  const showForm = !!layout && !missing;

  return (
    <Container>
      <PageDetailHeader
        title={title}
        parentLabel={t("layoutsView.title")}
        parentTo={pagePath}
        currentLabel={breadcrumbCurrent}
      />
      {loadError && (
        <InfoBox variant="error">{t("layoutsView.errorLoad")}</InfoBox>
      )}
      {(updateError || deleteError) && (
        <InfoBox variant="error">{t("layoutsView.errorSave")}</InfoBox>
      )}
      {successMessage && (
        <InfoBox variant="success">{successMessage}</InfoBox>
      )}
      <Card>
        {isLoading && !layout ? (
          <LoadingContainer message={t("layoutsView.loading")} />
        ) : missing || !showForm ? (
          <InfoBox variant="error">{t("catalog.itemNotFound")}</InfoBox>
        ) : (
          <>
            <LayoutForm
              key={layout.id}
              formId={FORM_ID}
              layout={layout}
              savedTick={savedTick}
              onSubmit={handleSubmit}
              onDirtyChange={setDirty}
            />
            <FormActions>
              <FormActionsStart>
                <Button
                  type="button"
                  $color="danger"
                  onClick={() => setDeleteOpen(true)}
                  disabled={isSaving || isDeletePending}
                >
                  {t("common.delete")}
                </Button>
              </FormActionsStart>
              <RouterButtonLink
                to={pagePath}
                $color="secondary"
                onClick={(event) => {
                  if (isSaving) event.preventDefault();
                }}
              >
                {t("common.cancel")}
              </RouterButtonLink>
              <Button type="submit" form={FORM_ID} disabled={isSaving}>
                {isSaving ? t("common.saving") : t("common.update")}
              </Button>
            </FormActions>
          </>
        )}
      </Card>
      <ConfirmDialog
        isOpen={deleteOpen}
        title={t("layoutForm.deleteLayout")}
        message={t("layoutsView.deleteConfirm", {
          name: layout?.name ?? "",
        })}
        confirmLabel={t("common.delete")}
        cancelLabel={t("common.cancel")}
        danger
        onConfirm={confirmDelete}
        onCancel={() => setDeleteOpen(false)}
      />
    </Container>
  );
};
